// apiService.js - Main entry point for all API services
// This file maintains backward compatibility while using the new modular structure

// Import everything for organized access
import * as apiConfig from './apiConfig';
import * as mealPlanService from './mealPlanService';
import * as recipeDetailsService from './recipeDetailsService';
import * as groceryListService from './groceryListService';
import * as sharedGroceryService from './sharedGroceryService';
// Import country-related functions
import { 
  normalizeCountryName, 
  DEFAULT_COUNTRY, 
  COUNTRY_STORAGE_KEY,
  detectCountryFromIP
} from './countryService';


// Export configuration and utilities
export { 
  ACTIVE_PROVIDER,
  MODEL_CONFIG,
  getProviderForModel,
  cleanJsonResponse,
} from './apiConfig';

// Export meal plan-related services
export {
  fetchMealPlan
} from './mealPlanService';

// Export recipe details-related services
export {
  fetchRecipeDetails,
  fetchAlternativeRecipes,
  getRecipeImage,
  getRecipeImageWithRecovery,
  resetImageStagger,
  extractJsonStringField,
  extractJsonObjectField,
  probeRunware
} from './recipeDetailsService';

// Export grocery list-related services
export {
  fetchEnhancedGroceryList,
  processGroceryListWithGemini,
  calculateCorrectPrice
} from './groceryListService';

export {
  createSharedGroceryLink,
  fetchSharedGroceryList,
  updateSharedGroceryState,
} from './sharedGroceryService';

// Global country state for the application - initialized with detected country
let _userCountry = null;
let _countryPromise = null;

/**
 * Initialize the user country when the application starts
 */
export const initializeUserCountry = () => {
  // If already initialized with a value, return it
  if (_userCountry) {
    return Promise.resolve(_userCountry);
  }
  
  // If we already have a promise in progress, return that
  if (_countryPromise) {
    return _countryPromise;
  }
  
  // Check if we have a country saved in localStorage
  const savedCountry = localStorage.getItem(COUNTRY_STORAGE_KEY);
  if (savedCountry) {
    console.log(`Country found in local storage: ${savedCountry}`);
    _userCountry = normalizeCountryName(savedCountry);
    _countryPromise = Promise.resolve(_userCountry);
    return _countryPromise;
  }
  
  // Start the detection process using IP
  console.log("Starting country detection using IP...");
  _countryPromise = detectCountryFromIP()
    .then(country => {
      console.log(`Country detected: ${country || DEFAULT_COUNTRY}`);
      _userCountry = country || DEFAULT_COUNTRY;
      
      // Save to localStorage for future sessions
      localStorage.setItem(COUNTRY_STORAGE_KEY, _userCountry);
      
      return _userCountry;
    })
    .catch(error => {
      console.error('Error detecting country:', error);
      _userCountry = DEFAULT_COUNTRY;
      return DEFAULT_COUNTRY;
    });
  
  return _countryPromise;
};

/**
 * Get the current user country asynchronously
 */
export const getUserCountry = async () => {
  // If we already have the country, return it immediately
  if (_userCountry) {
    return _userCountry;
  }
  
  // Otherwise, initialize and wait for it
  return initializeUserCountry();
};

/**
 * Get the current user country synchronously
 * Uses cached value or default if not yet determined
 */
export const getUserCountrySync = () => {
  // First check memory cache
  if (_userCountry) {
    return _userCountry;
  }
  
  // Then check local storage
  const savedCountry = localStorage.getItem(COUNTRY_STORAGE_KEY);
  if (savedCountry) {
    return normalizeCountryName(savedCountry);
  }
  
  // If all else fails, return default
  return DEFAULT_COUNTRY;
};

/**
 * Set the user country manually
 */
export const setUserCountry = async (country) => {
  if (!country || typeof country !== 'string') {
    console.warn('Attempted to set empty or invalid country, using default country');
    country = DEFAULT_COUNTRY;
  }
  
  // Normalize the country name
  const normalizedCountry = normalizeCountryName(country);
  console.log(`Setting user country to: ${country} (normalized: ${normalizedCountry})`);
  
  _userCountry = normalizedCountry;
  _countryPromise = Promise.resolve(normalizedCountry);
  
  // Save to localStorage for persistence
  localStorage.setItem(COUNTRY_STORAGE_KEY, normalizedCountry);

  countryChangeListeners.forEach(listener => {
    try {
      listener(normalizedCountry);
    } catch (e) {
      console.error('Error in country change listener:', e);
    }
  });
  
  return _userCountry;
};

/**
 * Reset the user country to auto-detected value
 */
export const resetUserCountry = async () => {
  // Clear the current values to force re-detection
  _userCountry = null;
  _countryPromise = null;
  
  // Clear from localStorage
  localStorage.removeItem(COUNTRY_STORAGE_KEY);
  
  // Re-initialize
  return initializeUserCountry();
};

// Automatically initialize user country when this module is imported
initializeUserCountry().catch(err => {
  console.error('Failed to initialize country on startup:', err);
});

const countryChangeListeners = [];

export const addCountryChangeListener = (listener) => {
  countryChangeListeners.push(listener);
};

export const removeCountryChangeListener = (listener) => {
  const index = countryChangeListeners.indexOf(listener);
  if (index !== -1) {
    countryChangeListeners.splice(index, 1);
  }
};

// Automatically initialize user country when this module is imported
initializeUserCountry();

/**
 * Organized service structure with all functionality
 * Provides a convenient namespaced API for all operations
 */
const services = {
  config: apiConfig,
  mealPlan: {
    fetch: mealPlanService.fetchMealPlan
  },
  recipeDetails: {
    fetch: recipeDetailsService.fetchRecipeDetails,
    generateImage: recipeDetailsService.getRecipeImage
  },
  groceryList: {
    fetch: groceryListService.fetchEnhancedGroceryList,
    processWithGemini: groceryListService.processGroceryListWithGemini,
    calculatePrice: groceryListService.calculateCorrectPrice
  },
  sharedGrocery: {
    createLink: sharedGroceryService.createSharedGroceryLink,
    fetch: sharedGroceryService.fetchSharedGroceryList,
    updateState: sharedGroceryService.updateSharedGroceryState,
  },
  country: {
    get: getUserCountry,
    set: setUserCountry,
    reset: resetUserCountry
  }
};

export { services };

// This approach maintains the existing imports while providing a better organized structure
// Example: import { fetchMealPlan } from './apiService'; // Still works
// Example: import { services } from './apiService'; // New organized structure